import React, { useState, useEffect } from 'react';
import { X, Search, CheckCircle2, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';

const TireSelectionModal = ({ isOpen, onClose, vehicle, position, positionLabel, onInstallSuccess }) => {
  const [availableTires, setAvailableTires] = useState([]);
  const [filteredTires, setFilteredTires] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTire, setSelectedTire] = useState(null);
  const [loading, setLoading] = useState(false);
  const [installing, setInstalling] = useState(false);

  // Dados de instalação
  const [installationData, setInstallationData] = useState({
    odometer: vehicle?.current_odometer != null ? vehicle.current_odometer : '',
    treadDepth: '',
    installerName: ''
  });

  // Atualizar odômetro quando modal abrir com novo veículo
  useEffect(() => {
    if (isOpen && vehicle) {
      const odometerValue = vehicle.current_odometer != null ? vehicle.current_odometer : '';
      console.log('🚗 Modal abriu com veículo:', vehicle);
      console.log('📊 Odometer do veículo (current_odometer):', vehicle.current_odometer);
      console.log('📊 Odometer setado:', odometerValue);
      setInstallationData({
        odometer: odometerValue,
        treadDepth: '',
        installerName: ''
      });
    }
  }, [isOpen, vehicle]);

  // Buscar pneus disponíveis
  useEffect(() => {
    if (isOpen) {
      fetchAvailableTires();
    }
  }, [isOpen]);

  // Filtrar pneus conforme busca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTires(availableTires);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = availableTires.filter(tire =>
        tire.serial_number?.toLowerCase().includes(term) ||
        tire.brand?.toLowerCase().includes(term) ||
        tire.model?.toLowerCase().includes(term) ||
        tire.size?.toLowerCase().includes(term)
      );
      setFilteredTires(filtered);
    }
  }, [searchTerm, availableTires]);

  const fetchAvailableTires = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/tires', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          status: 'in_stock,storage', // Apenas pneus disponíveis
          limit: 100 // Mostrar bastante opções
        }
      });

      if (response.data.success) {
        setAvailableTires(response.data.data || []);
        setFilteredTires(response.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar pneus:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async () => {
    // Validação de tire selecionado
    if (!selectedTire || !selectedTire.id) {
      alert('Selecione um pneu');
      console.error('❌ selectedTire inválido:', selectedTire);
      return;
    }

    // Validação de veículo
    if (!vehicle || !vehicle.id) {
      alert('Erro: Veículo não identificado');
      console.error('❌ vehicle inválido:', vehicle);
      return;
    }

    // Validação de posição
    if (!position) {
      alert('Erro: Posição não identificada');
      console.error('❌ position inválida:', position);
      return;
    }

    // Validação de odômetro
    if (!installationData.odometer && installationData.odometer !== 0) {
      alert('Informe o odômetro atual');
      return;
    }

    const odometerValue = parseInt(installationData.odometer);
    if (isNaN(odometerValue)) {
      alert('Odômetro inválido. Use apenas números.');
      console.error('❌ odometer NaN:', installationData.odometer);
      return;
    }

    setInstalling(true);
    try {
      const token = localStorage.getItem('token');

      const payload = {
        tire_id: selectedTire.id,
        vehicle_id: vehicle.id,
        position: position,
        installation_odometer: odometerValue,
        installation_tread_depth_mm: installationData.treadDepth ? parseFloat(installationData.treadDepth) : null,
        installer_name: installationData.installerName || null,
        installation_date: new Date().toISOString().split('T')[0]
      };

      console.log('📤 Payload de instalação:', payload);
      console.log('📋 Campos obrigatórios:', {
        tire_id: payload.tire_id,
        vehicle_id: payload.vehicle_id,
        position: payload.position,
        installation_odometer: payload.installation_odometer
      });

      const response = await axios.post('/api/tires/install', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        onInstallSuccess && onInstallSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Erro ao instalar pneu:', error);
      alert('Erro ao instalar pneu: ' + (error.response?.data?.message || error.message));
    } finally {
      setInstalling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Instalar Pneu
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {vehicle?.brand} {vehicle?.model} - {vehicle?.license_plate}
            </p>
            <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-1">
              Posição: {positionLabel} ({position})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por número de série, marca, modelo ou tamanho..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Tire List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-500 dark:text-gray-400 mt-4">Carregando pneus...</p>
            </div>
          ) : filteredTires.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Nenhum pneu encontrado com esse critério' : 'Nenhum pneu disponível em estoque'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {filteredTires.map(tire => (
                <button
                  key={tire.id}
                  onClick={() => {
                    console.log('🔧 Pneu selecionado:', tire);
                    setSelectedTire(tire);
                  }}
                  className={`
                    p-4 border-2 rounded-lg text-left transition-all
                    ${selectedTire?.id === tire.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 dark:text-white">
                        {tire.brand} {tire.model}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        S/N: {tire.serial_number}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Tamanho: {tire.size}
                      </p>
                      {tire.current_tread_depth_mm && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Profundidade: {tire.current_tread_depth_mm}mm
                        </p>
                      )}
                      {tire.warehouse_location && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Localização: {tire.warehouse_location}
                        </p>
                      )}
                    </div>
                    {selectedTire?.id === tire.id && (
                      <CheckCircle2 className="w-6 h-6 text-blue-500 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Installation Data Form */}
          {selectedTire && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Dados da Instalação
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Odômetro Atual (km) *
                  </label>
                  <input
                    type="number"
                    value={installationData.odometer}
                    onChange={(e) => setInstallationData({...installationData, odometer: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Profundidade Inicial (mm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={installationData.treadDepth}
                    onChange={(e) => setInstallationData({...installationData, treadDepth: e.target.value})}
                    placeholder={selectedTire.current_tread_depth_mm || 'Ex: 8.0'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Instalador
                  </label>
                  <input
                    type="text"
                    value={installationData.installerName}
                    onChange={(e) => setInstallationData({...installationData, installerName: e.target.value})}
                    placeholder="Nome do instalador"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900 dark:bg-opacity-20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  Verifique se o pneu está em boas condições antes de instalar. A profundidade mínima recomendada é 3mm.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800
                     transition-colors"
            disabled={installing}
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              console.log('🎯 Estado atual:', {
                selectedTire: selectedTire?.id,
                odometer: installationData.odometer,
                installing
              });
              handleInstall();
            }}
            disabled={!selectedTire || (!installationData.odometer && installationData.odometer !== 0) || installing}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg
                     hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed
                     transition-colors flex items-center gap-2"
          >
            {installing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Instalando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Instalar Pneu
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TireSelectionModal;
